import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Clock, User, DollarSign, AlertCircle, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getHomePath } from "@/utils/localePaths";
const API_URL = `${BACKEND_URL}/api`;

function PendingReportsPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { locale } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingReports, setPendingReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  // Check if user is team member
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(getLoginPath(locale));
      return;
    }

    if (!user.email || !user.email.toLowerCase().endsWith('@personaajans.com')) {
      toast.error('Bu sayfaya erişim yetkiniz yok.');
      navigate(getHomePath(locale));
      return;
    }

    fetchPendingReports();
  }, [user, navigate, authLoading]);

  const fetchPendingReports = async () => {
    try {
      setLoading(true);
      
      if (!token) {
        toast.error('Token bulunamadı. Lütfen tekrar giriş yapın.');
        navigate(getLoginPath(locale));
        return;
      }
      
      console.log('=== PENDING REPORTS DEBUG ===');
      console.log('BACKEND_URL:', BACKEND_URL);
      console.log('Full URL:', `${BACKEND_URL}/api/team/pending-reports`);
      console.log('Token exists:', !!token);
      console.log('Token (first 30 chars):', token?.substring(0, 30));
      console.log('=============================');
      
      const response = await axios.get(`${BACKEND_URL}/api/team/pending-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Pending reports response:', response.data);
      setPendingReports(response.data);
    } catch (error) {
      console.error('Error fetching pending reports:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Bekleyen raporlar yüklenemedi';
      toast.error(`Hata: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeadline = (dateString) => {
    const date = new Date(dateString);
    const deadline = new Date(date.getTime() + 2 * 60 * 60 * 1000); // +2 saat
    return deadline.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAssignReport = async (reportId, e) => {
    e.stopPropagation();
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/team/assign-report/${reportId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Rapor başarıyla üstlenildi!');
      
      // Refresh list
      fetchPendingReports();
    } catch (error) {
      console.error('Error assigning report:', error);
      toast.error(error.response?.data?.detail || 'Rapor üstlenilirken hata oluştu');
    }
  };

  const getStatusBadge = (status, assignedToEmail) => {
    if (status === 'beklemede' || !status || status === 'pending') {
      return (
        <span className="bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-bold">
          BEKLİYOR
        </span>
      );
    } else if (status === 'devam_ediyor') {
      return (
        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
          <span>DEVAM EDİYOR</span>
          {assignedToEmail && <span className="text-xs">({assignedToEmail.split('@')[0]})</span>}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gray-800 bg-black/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/opara-seffaf-logo.png?v=20251120" alt="OPARA" className="h-8 w-auto" />
            <span className="text-lg font-bold text-[#FFDD59]">OPARA</span>
          </Link>

          {/* User Menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">
                  <User className="w-5 h-5" />
                  <span className="font-medium">{user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-gray-900 border-gray-800 text-white">
                <DropdownMenuLabel>Hesabım</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-800" />
                <DropdownMenuItem onClick={() => navigate('/hesap-ayarlari')} className="hover:bg-gray-800 cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Hesap Ayarları
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(getHomePath(locale))} className="hover:bg-gray-800 cursor-pointer">
                  <FileText className="w-4 h-4 mr-2" />
                  Ana Sayfa
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-gray-800" />
                <DropdownMenuItem onClick={() => {
                  localStorage.removeItem('token');
                  navigate(getLoginPath(locale));
                }} className="hover:bg-gray-800 cursor-pointer text-red-400">
                  <LogOut className="w-4 h-4 mr-2" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-8 mt-20">
        {/* Header with Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {/* Left - Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Geri Git
            </button>

            {/* Right - Home Button */}
            <button
              onClick={() => navigate(getHomePath(locale))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold transition-colors"
            >
              Ana Sayfaya Dön
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>

          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#FFDD59]" />
            Bekleyen Raporlar
          </h1>
          <p className="text-gray-400 flex items-center gap-2">
            <span>OPARA Yapsın talepleri - Manuel işlem gerektiren raporlar</span>
            {!loading && pendingReports.length > 0 && (
              <span className="text-[#FFDD59] font-semibold">
                • Bekleyen Rapor Sayısı: {pendingReports.length}
              </span>
            )}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFDD59] mx-auto"></div>
            <p className="text-gray-400 mt-4">Yükleniyor...</p>
          </div>
        ) : pendingReports.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Bekleyen Rapor Yok
            </h3>
            <p className="text-gray-500">
              Şu anda işlem bekleyen rapor bulunmuyor.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {pendingReports.map((report) => (
              <div
                key={report.report_id}
                className="bg-gray-900 border border-gray-800 hover:border-[#FFDD59] rounded-lg p-6 transition-all cursor-pointer"
                onClick={() => navigate(`/bekleyen-raporlar/${report.report_id}`)}
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Compact Info Row */}
                  <div className="flex-1 flex items-center gap-6 flex-wrap">
                    {/* Status Badge */}
                    {getStatusBadge(report.status, report.assigned_to_email)}

                    {/* Talep Kodu Badge */}
                    <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-mono">
                      {report.talep_kodu}
                    </div>

                    {/* User */}
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#FFDD59]" />
                      <div>
                        <span className="text-sm font-semibold">{report.user_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{report.user_email}</span>
                      </div>
                    </div>

                    {/* Influencer Count */}
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#FFDD59]" />
                      <span className="text-sm font-semibold">{report.influencer_count} kişi</span>
                    </div>

                    {/* Credit */}
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#FFDD59]" />
                      <span className="text-sm font-semibold">{report.credit_cost} kredi</span>
                    </div>

                    {/* Request Time & Deadline */}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#FFDD59]" />
                      <div className="text-sm">
                        <span className="text-gray-400">Talep:</span>
                        <span className="font-semibold ml-1">{formatDate(report.created_at)}</span>
                        <span className="text-red-400 ml-2">→ Termin:</span>
                        <span className="font-semibold text-red-400 ml-1">{getDeadline(report.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {/* Show "Raporu Üstlen" if beklemede */}
                    {(report.status === 'beklemede' || !report.status || report.status === 'pending') && (
                      <button
                        onClick={(e) => handleAssignReport(report.report_id, e)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        Raporu Üstlen
                      </button>
                    )}
                    
                    {/* Raporu İşle - Redirect to HomePage with talep_kodu */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/?talep_kodu=${report.talep_kodu}`);
                      }}
                      className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold px-6 py-2 rounded-lg transition-colors whitespace-nowrap"
                      title="Ana sayfada bu rapor için sorgu yapın"
                    >
                      Raporu İşle →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-900/20 border border-blue-700 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <p className="font-semibold mb-1">Bilgilendirme</p>
            <p>
              Bu sayfada sadece "OPARA Yapsın" özelliği ile gönderilen talepler görünür. 
              Kullanıcıların manuel view count girip analiz talep ettikleri raporlar bu listede yer almaz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PendingReportsPage;
